/**
 * Round 114 (Phase C) — Brand Ready success screen.
 *
 * Renders AFTER the user selects their primary logo concept. Shows
 * a celebratory checklist of what was created and a single big CTA
 * that takes them into the Sales Page Builder to start building
 * their website with the brand already applied.
 *
 * This is the "wow moment" — the user completes onboarding with a
 * full, usable brand identity, not just a logo.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle, Sparkle, ArrowRight, Download, House,
} from '@phosphor-icons/react';
import api, { brandBrainAPI } from '@/utils/api';

const CheckRow = ({ label, done, testid }) => (
  <div
    data-testid={testid}
    className={`flex items-center gap-3 py-2.5 border-b border-slate-800 last:border-0 ${
      done ? 'text-slate-100' : 'text-slate-500'
    }`}
  >
    <CheckCircle
      size={22}
      weight={done ? 'fill' : 'regular'}
      className={done ? 'text-emerald-400' : 'text-slate-600'}
    />
    <span className="text-base">{label}</span>
  </div>
);

const BrandReadyPage = () => {
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  // Round 218D P0 (2026-02-07) — CTA label + destination come
  // strictly from the canonical resolver. If it fails we bail to
  // /dashboard (safe surface), never to a hardcoded builder route.
  const [nextAction, setNextAction] = useState({
    label: 'Loading next step…',
    route: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await brandBrainAPI.get();
        if (!cancelled) setBrand(d?.brand_brain || null);
      } catch (err) {
        console.warn(err);
      }
      try {
        const res = await api.get('/foundation/next-action');
        const act = res?.data?.action;
        if (!cancelled && act) {
          setNextAction({
            label: act.next_cta_label || 'Continue',
            route: act.next_route || '/dashboard',
          });
        } else if (!cancelled) {
          setNextAction({ label: 'Open your Dashboard', route: '/dashboard' });
        }
      } catch (err) {
        console.warn('next-action prefetch failed:', err);
        if (!cancelled) {
          setNextAction({ label: 'Open your Dashboard', route: '/dashboard' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const primaryLogo = (brand?.logo_urls || []).find(
    (l) => l.is_primary && l.kind === 'concept',
  );
  const guidelinesPdf = (brand?.brand_guidelines?.guideline_urls || []).slice(-1)[0];

  const checks = [
    {
      label: 'Logo created',
      done: !!primaryLogo,
      testid: 'ready-check-logo',
    },
    {
      label: 'Colors selected',
      done: (brand?.primary_colors?.length || 0) > 0,
      testid: 'ready-check-colors',
    },
    {
      label: 'Fonts selected',
      done: !!(brand?.fonts?.heading || brand?.fonts?.body),
      testid: 'ready-check-fonts',
    },
    {
      label: 'Brand voice created',
      done: !!brand?.brand_voice,
      testid: 'ready-check-voice',
    },
    {
      label: 'Business Brain complete',
      done: true, // If they got here, Business Brain was already done.
      testid: 'ready-check-brain',
    },
  ];

  // Round 218D P0 (2026-02-07) — route via canonical next-action
  // resolver ONLY. No hardcoded builder route as fallback; if the
  // resolver has not resolved (or errored) we bail to /dashboard,
  // never to a specific builder page.
  const goBuildWebsite = async () => {
    if (!nextAction.route) {
      navigate('/dashboard');
      return;
    }
    try {
      toast.success('Your brand is applied — let\'s build your first asset.');
    } catch (_) { /* noop */ }
    navigate(nextAction.route);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-10"
      data-testid="brand-ready-page"
    >
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1 text-xs uppercase tracking-widest text-emerald-300 mb-4">
            <Sparkle size={14} weight="fill" /> PayGBot
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold text-white mb-3">
            🎉 Your brand is ready
          </h1>
          <p className="text-slate-400 text-lg">
            Every builder now uses this identity automatically.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-slate-400 text-sm">
            Loading your brand…
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8">
            {primaryLogo && (
              <div className="mb-6 rounded-xl border border-slate-700 bg-slate-800/40 p-8 flex items-center justify-center">
                <img
                  src={primaryLogo.url}
                  alt="Primary logo"
                  className="max-h-40 max-w-md object-contain"
                  data-testid="ready-primary-logo"
                />
              </div>
            )}

            <div className="mb-6">
              {checks.map((c) => (
                <CheckRow key={c.label} label={c.label} done={c.done} testid={c.testid} />
              ))}
            </div>

            <button
              onClick={goBuildWebsite}
              disabled={!nextAction.route}
              data-testid="ready-build-website"
              data-next-route={nextAction.route || ''}
              className="w-full rounded-lg bg-emerald-500 py-4 text-lg font-semibold text-white hover:bg-emerald-400 transition flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {nextAction.label} <ArrowRight size={20} />
            </button>

            <div className="mt-4 flex items-center justify-between text-sm">
              {guidelinesPdf?.url ? (
                <a
                  href={guidelinesPdf.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ready-download-guidelines"
                  className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300"
                >
                  <Download size={16} /> Download Brand Guidelines PDF
                </a>
              ) : (
                <span className="text-slate-600">Guidelines PDF generating…</span>
              )}

              <button
                onClick={() => navigate('/dashboard')}
                data-testid="ready-go-dashboard"
                className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-200"
              >
                <House size={16} /> Skip to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandReadyPage;
